import { Route, Routes } from '@angular/router';
import { QuizWrapperComponent } from './layout/quiz-wrapper/quiz-wrapper.component';
import { QUIZ_NAVIGATION_ITEMS } from './config/quiz-navigation-config';
import { primaryAuthGuard } from './guards/primary-auth.guard';
import { EntryComponent } from './home/components/entry/entry.component';
import { LoginRequiredComponent } from './home/components/login-required/login-required.component';
import { requiresFragmentGuard } from './guards/requires-fragment.guard';
import { NotFoundComponent } from './home/components/not-found/not-found.component';
import { HomeComponent } from './home/home/home.component';
import { environment } from '../environments/environment';
import { DeveloperDebuggerComponent } from './debug/developer-debugger/developer-debugger.component';
import { QuizDetailsFormComponent } from './quizzes/quiz-details/components/quiz-details-form/quiz-details-form.component';
import { QuizDetailsFormContainerComponent } from './quizzes/quiz-details/containers/quiz-details-form-container/quiz-details-form-container.component';
import { FormDirtyGuard } from './guards/form-dirty.guard';
import { UserQuizReportContainerComponent } from './user-quiz-report/containers/user-quiz-report-container.component';

// Updated to use dynamic imports for lazy loading
const quizComponentMap: Record<string, {
  loadComponent: () => Promise<any>,
  canDeactivate?: boolean // Flag to indicate if the route should have FormDirtyGuard
}> = {
  '': {
    loadComponent: () => import('./quizzes/quiz-details/containers/quiz-details-form-container/quiz-details-form-container.component')
      .then(m => m.QuizDetailsFormContainerComponent),
    canDeactivate: true
  },
  'announcements': {
    loadComponent: () => import('./quizzes/announcements/containers/announcements-container/announcements-container.component')
      .then(m => m.AnnouncementsFormContainerComponent),
    canDeactivate: true
  },
  'questions': {
    loadComponent: () => import('./quizzes/quiz-questions/containers/quiz-questions-configuration-container/quiz-questions-configuration-container.component')
      .then(m => m.QuizQuestionsConfigurationContainerComponent),
    canDeactivate: true
  },
  'publication': {
    loadComponent: () => import('./quizzes/quiz-publish/quiz-publish.component')
      .then(m => m.QuizPublishComponent),
    canDeactivate: true
  },
  'learners': {
    loadComponent: () => import('./quizzes/quiz-learners/containers/quiz-learners-container.component')
      .then(m => m.QuizLearnersContainerComponent)
  },
  'reports': {
    loadComponent: () => import('./quizzes/quiz-reports/containers/quiz-reports.component')
      .then(m => m.QuizReportsComponent)
  },
};

// Function to build the quiz routes using the shared navigation items
function buildQuizRoutes(): Routes {
  return QUIZ_NAVIGATION_ITEMS
    .map(item => {
      const routeConfig = quizComponentMap[item.path];

      if (!routeConfig) {
        console.warn(`No component mapping found for route: ${item.path}`);
        return null;
      }

      const route: Route = {
        path: item.path,
        loadComponent: routeConfig.loadComponent,
        data: {
          ...item.data,
          navigationItem: item
        }
      };

      // Only add the canDeactivate guard if specified
      if (routeConfig.canDeactivate) {
        route.canDeactivate = [FormDirtyGuard];
      }

      return route;
    }).filter(route => route !== null) as Routes;
}

const devRoutes = [];
if (!environment.production) {
  devRoutes.push({ path: 'debug', component: DeveloperDebuggerComponent, pathMatch: 'full' } as Route);
}

export const routes: Routes = [
  ...devRoutes,
  {
    path: 'quizzes',
    canActivate: [primaryAuthGuard],
    component: QuizWrapperComponent,
    children: [
      {
        path: 'new',
        component: QuizDetailsFormContainerComponent,
      },
      {
        path: ':quizGuid',
        children: buildQuizRoutes()
      },
      { path: ':quizGuid/learners/:userId/quiz-report', component: UserQuizReportContainerComponent, canActivate: [primaryAuthGuard] }
    ]
  },
  { path: 'entry', pathMatch: 'full', component: EntryComponent, canActivate: [requiresFragmentGuard] },
  { path: 'login', component: LoginRequiredComponent },
  { path: '', pathMatch: 'full', component: HomeComponent, canActivate: [primaryAuthGuard] },
  { path: '**', component: NotFoundComponent, pathMatch: 'full' },
];
